import { useParams, Link, Navigate } from "react-router";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { blogPosts } from "../data/blogPosts";
import { Calendar, User, ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function BlogPost() {
  const { id } = useParams<{ id: string }>();
  const post = blogPosts.find((p) => p.id === id);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": "https://unpatterned.org/og-image.png",
    "datePublished": post.date,
    "author": {
      "@type": "Person",
      "name": post.author.name,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Unpatterned",
      "logo": {
        "@type": "ImageObject",
        "url": "https://unpatterned.org/logo.png",
      },
    },
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title={`${post.title} - Unpatterned`}
        description={post.excerpt}
        keywords={[
          post.category,
          "unpatterned",
          "announcement",
          "technology education",
          "cognitive depth",
        ]}
        url={`https://unpatterned.org/blog/${post.id}`}
        type="article"
        author={post.author.name}
        publishedTime={post.date}
        structuredData={structuredData}
      />
      <Header />

      {/* Back Button */}
      <section className="pt-32 px-6">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#184b9d] font-['Manrope',sans-serif] font-medium hover:gap-3 transition-all mb-8"
          >
            <ArrowLeft size={20} />
            Back to all posts
          </Link>
        </div>
      </section>

      {/* Article Header */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <header className="mb-12">
            <h1 className="font-['DM_Serif_Display',serif] text-5xl text-[#050e1d] mb-4 leading-tight">
              {post.title}
            </h1>
            <p className="font-['Manrope',sans-serif] text-2xl text-[#050e1d]/60 mb-8 italic">
              {post.subtitle}
            </p>

            <div className="flex items-center gap-6 text-sm font-['Manrope',sans-serif] text-[#050e1d]/50 pb-8 border-b border-[rgba(5,14,26,0.1)]">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                {formatDate(post.date)}
              </div>
              <div className="flex items-center gap-2">
                <User size={16} />
                <div>
                  <span className="text-[#050e1d]/80 font-medium">{post.author.name}</span>
                  <span className="text-[#050e1d]/50"> • {post.author.role}</span>
                </div>
              </div>
            </div>
          </header>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => (
                  <h1 className="font-['DM_Serif_Display',serif] text-4xl text-[#050e1d] mt-12 mb-6 leading-tight">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="font-['DM_Serif_Display',serif] text-3xl text-[#050e1d] mt-10 mb-5 leading-tight">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="font-['Manrope',sans-serif] font-semibold text-2xl text-[#050e1d] mt-8 mb-4">
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80 leading-relaxed mb-6">
                    {children}
                  </p>
                ),
                ul: ({ children }) => (
                  <ul className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80 leading-relaxed mb-6 space-y-2 list-disc pl-6">
                    {children}
                  </ul>
                ),
                li: ({ children }) => (
                  <li className="font-['Manrope',sans-serif]">{children}</li>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-[#050e1d]">{children}</strong>
                ),
                a: ({ href, children }) => (
                  <a
                    href={href}
                    className="text-[#184b9d] hover:text-[#1e8eaf] underline transition-colors"
                  >
                    {children}
                  </a>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-[#184b9d] pl-6 py-2 my-6 italic text-[#050e1d]/70">
                    {children}
                  </blockquote>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto my-8 rounded-xl border border-[#050e1d]/10">
                    <table className="w-full border-collapse font-['Manrope',sans-serif] text-left">
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead className="bg-[#184b9d] text-white">{children}</thead>
                ),
                th: ({ children }) => (
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="border-t border-[#050e1d]/10 px-5 py-3 text-[#050e1d]/80 whitespace-nowrap">
                    {children}
                  </td>
                ),
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Share Section */}
          <div className="mt-16 pt-8 border-t border-[rgba(5,14,26,0.1)]">
            <p className="font-['Manrope',sans-serif] text-sm text-[#050e1d]/50 mb-4">
              Share this article
            </p>
            <div className="flex gap-3">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(`https://unpatterned.org/blog/${post.id}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-[rgba(24,75,157,0.1)] text-[#184b9d] rounded-lg font-['Manrope',sans-serif] text-sm font-medium hover:bg-[rgba(24,75,157,0.2)] transition-colors"
              >
                Share on Twitter
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://unpatterned.org/blog/${post.id}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-[rgba(24,75,157,0.1)] text-[#184b9d] rounded-lg font-['Manrope',sans-serif] text-sm font-medium hover:bg-[rgba(24,75,157,0.2)] transition-colors"
              >
                Share on LinkedIn
              </a>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
